// client/src/components/forms/FormsPage.jsx
//
// /:tenant/forms[?event=<eventId>] — interest / lead forms ("Get notified",
// "I'm interested", RSVP, volunteer, pre-register) attached to an event or
// the public hub. Lists forms with submission counts and creates new ones;
// each opens in FormEditor. Growth Phase 4, feature_key "forms".
//
// API: /api/forms/:tenant/forms

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { getJson } from '../../services/http';
import { createForm, listForms, PURPOSES } from '../../services/formsApi';
import '../analytics/PerformancePanel.css';
import './Forms.css';

export function purposeLabel(t, p) {
  const labels = {
    notify: t('forms.purpose.notify', 'Get notified'),
    interest: t('forms.purpose.interest', "I'm interested"),
    rsvp: t('forms.purpose.rsvp', 'RSVP'),
    volunteer: t('forms.purpose.volunteer', 'Volunteer'),
    preregister: t('forms.purpose.preregister', 'Pre-register'),
    other: t('forms.purpose.other', 'Other'),
  };
  return labels[p] || p;
}

export function notReadyMessage(t, err) {
  return /^409/.test(err?.message || '') || err?.response?.status === 409
    ? t('forms.notReady', 'Forms need a database update before they can be used.')
    : null;
}

export default function FormsPage({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const tenant = Number(tenantProp || params.tenant);
  const eventFilter = search.get('event') || '';

  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [draft, setDraft] = useState({ name: '', purpose: 'notify', target: eventFilter ? `event:${eventFilter}` : 'hub' });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await listForms(tenant, { eventId: eventFilter || undefined });
      setRows(res.data || []);
    } catch (err) {
      setError(notReadyMessage(t, err) || t('forms.loadError', 'Could not load forms.'));
    }
  }, [tenant, eventFilter, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getJson(`/api/events/${tenant}/events`)
      .then((d) => setEvents(d.data || d.events || []))
      .catch(() => setEvents([]));
  }, [tenant]);

  const create = async (e) => {
    e.preventDefault();
    setNotice(null);
    setSaving(true);
    try {
      const [targetType, targetId] = draft.target.split(':');
      const res = await createForm(tenant, {
        name: draft.name.trim(),
        purpose: draft.purpose,
        targetType,
        targetId: targetId ? Number(targetId) : undefined,
        title: purposeLabel(t, draft.purpose),
      });
      navigate(`/${tenant}/forms/${res.data.id}`);
    } catch (err) {
      setNotice(err?.response?.data?.error || t('forms.createError', 'Could not create the form.'));
    } finally {
      setSaving(false);
    }
  };

  const eventName = (id) => events.find((ev) => String(ev.id) === String(id))?.name;

  const navButtons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
  ];

  return (
    <div className="fo-page">
      <DesktopActionBar buttons={navButtons} />
      <section className="surface">
        <h2>{t('forms.title', 'Forms')}</h2>
        <p className="surface-muted perf-small">
          {t('forms.intro', 'Collect names and emails before anything goes on sale: "Get notified", RSVP, volunteer and pre-register forms for an event or your public page. The same email twice updates one contact.')}
        </p>
        {eventFilter && (
          <p className="perf-small">
            {t('forms.filteredToEvent', 'Showing forms for {{event}}.', { event: eventName(eventFilter) || `#${eventFilter}` })}{' '}
            <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenant}/forms`)}>
              {t('forms.showAll', 'Show all forms')}
            </button>
          </p>
        )}
      </section>

      <section className="surface">
        <h4>{t('forms.new', 'New form')}</h4>
        <form className="fo-form-grid" onSubmit={create}>
          <label className="fo-field">
            {t('forms.field.name', 'Name (staff only)')}
            <input value={draft.name} required maxLength={150} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('forms.field.namePlaceholder', 'Spring gala interest list')} />
          </label>
          <label className="fo-field">
            {t('forms.field.purpose', 'Purpose')}
            <select value={draft.purpose} onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}>
              {PURPOSES.map((p) => <option key={p} value={p}>{purposeLabel(t, p)}</option>)}
            </select>
          </label>
          <label className="fo-field">
            {t('forms.field.attachTo', 'Attach to')}
            <select value={draft.target} onChange={(e) => setDraft({ ...draft, target: e.target.value })}>
              <option value="hub">{t('forms.target.hub', 'Public page (not one event)')}</option>
              {events.map((ev) => <option key={ev.id} value={`event:${ev.id}`}>{ev.name}</option>)}
            </select>
          </label>
          <div className="fo-field" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="lm-btn" disabled={saving || !draft.name.trim()}>
              {saving ? t('forms.creating', 'Creating…') : t('forms.create', 'Create form')}
            </button>
          </div>
        </form>
        {notice && <div className="note error" role="alert">{notice}</div>}
      </section>

      <section className="surface">
        <h4>{t('forms.yourForms', 'Your forms')}</h4>
        {error && <ErrorState message={error} onRetry={load} />}
        {!error && rows === null && <LoadingState />}
        {!error && rows && rows.length === 0 && (
          <p className="surface-muted perf-small">{t('forms.empty', 'No forms yet. Create one above.')}</p>
        )}
        {!error && rows && rows.length > 0 && (
          <div className="perf-table-wrap">
            <table className="perf-table">
              <thead>
                <tr>
                  <th>{t('forms.col.name', 'Form')}</th>
                  <th>{t('forms.col.purpose', 'Purpose')}</th>
                  <th>{t('forms.col.attachedTo', 'Attached to')}</th>
                  <th className="perf-num">{t('forms.col.submissions', 'Submissions')}</th>
                  <th>{t('forms.col.status', 'Status')}</th>
                  <th aria-label={t('forms.col.actions', 'Actions')} />
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td>{f.name}</td>
                    <td>{purposeLabel(t, f.purpose)}</td>
                    <td>{f.targetType === 'event' ? (f.eventName || `#${f.targetId}`) : t('forms.target.hubShort', 'Public page')}</td>
                    <td className="perf-num">{f.submissionCount ?? 0}</td>
                    <td>
                      <span className="lm-badge">{f.active ? t('forms.status.open', 'Open') : t('forms.status.closed', 'Closed')}</span>
                    </td>
                    <td>
                      <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenant}/forms/${f.id}`)}>
                        {t('forms.open', 'Open')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AppFooter buttons={navButtons} />
    </div>
  );
}
